import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CopilotChatResponse,
  CopilotConversationSummary,
  CopilotService,
  CopilotStatus,
} from '../../../services/copilot/copilot.service';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
  status?: CopilotStatus;
  partial?: boolean;
  omittedCount?: number;
  entities?: string[];
  failedQuestion?: string;
  createdAt: Date;
}

@Component({
  standalone: false,
  selector: 'app-copilot-chat',
  templateUrl: './copilot-chat.component.html',
  styleUrls: ['./copilot-chat.component.css'],
})
export class CopilotChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesViewport') messagesViewport?: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;

  visible = false;
  enabled = false;
  hasAccess = false;
  accessMessage = '';
  open = false;
  historyOpen = false;
  loading = false;
  loadingLabel = 'Analizando consulta';
  message = '';
  maxMessageLength = 4000;
  canAdmin = false;
  conversationId: string | null = null;
  conversations: CopilotConversationSummary[] = [];
  messages: UiMessage[] = [];
  private activeRequest?: Subscription;
  private statusTimer?: ReturnType<typeof setInterval>;

  readonly launcherSize = 58;

  floatingPosition = {
    x: 0,
    y: 0,
  };

  panelPosition = {
    x: 0,
    y: 0,
  };

  dragging = false;
  isCompactViewport = false;

  private dragPointerId: number | null = null;
  private dragSource: 'launcher' | 'header' | null = null;

  private dragStart = {
    pointerX: 0,
    pointerY: 0,
    positionX: 0,
    positionY: 0,
    panelPositionX: 0,
    panelPositionY: 0,
  };

  private dragMoved = false;
  private ignoreLauncherClick = false;

  private readonly floatingPositionStorageKey =
    'zivo:kyara:floating-position';

  constructor(
    private readonly copilotService: CopilotService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    if (!this.authService.hasValidToken()) return;
    this.copilotService.status().subscribe({
      next: (response) => {
        this.enabled = response.enabled;
        this.hasAccess = response.hasAccess === true;
        this.accessMessage =
          response.accessMessage ||
          'No tienes permisos asignados para consultar información con Kyara.';
        this.canAdmin = response.canAdmin === true;
        this.visible = response.authenticated && (this.enabled || this.canAdmin);
        this.maxMessageLength = response.maxMessageLength || 4000;
        if (this.visible) {
          this.initializeFloatingPosition();
          this.newConversation(false);
        }
      },
      error: () => {
        this.visible = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.stopGeneration();
  }

  toggle(): void {
    this.open = !this.open;

    if (this.open) {
      this.updatePanelPosition();

      setTimeout(() => {
        this.messageInput?.nativeElement.focus();
      }, 100);
    } else {
      this.historyOpen = false;
    }
  }

  onLauncherClick(event: MouseEvent): void {
    if (this.ignoreLauncherClick) {
      event.preventDefault();
      event.stopPropagation();
      this.ignoreLauncherClick = false;
      return;
    }

    this.toggle();
  }

  startDrag(
    event: PointerEvent,
    source: 'launcher' | 'header',
  ): void {
    if (!event.isPrimary || event.button !== 0) return;

    // En móvil el panel conserva el comportamiento de pantalla completa.
    // Solo se permite mover el launcher cuando el chat está cerrado.
    if (this.isCompactViewport && source === 'header') return;

    const target = event.target as HTMLElement;

    // Evitar iniciar arrastre al presionar los botones del encabezado.
    if (
      source === 'header' &&
      target.closest('button, input, textarea, select, a')
    ) {
      return;
    }

    event.preventDefault();

    this.dragging = true;
    this.dragMoved = false;
    this.dragPointerId = event.pointerId;
    this.dragSource = source;

    this.dragStart = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      positionX: this.floatingPosition.x,
      positionY: this.floatingPosition.y,
      panelPositionX: this.panelPosition.x,
      panelPositionY: this.panelPosition.y,
    };
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (
      !this.dragging ||
      this.dragPointerId === null ||
      event.pointerId !== this.dragPointerId
    ) {
      return;
    }

    const differenceX = event.clientX - this.dragStart.pointerX;
    const differenceY = event.clientY - this.dragStart.pointerY;

    if (!this.dragMoved && Math.hypot(differenceX, differenceY) < 4) {
      return;
    }

    this.dragMoved = true;
    event.preventDefault();

    this.floatingPosition = this.clampFloatingPosition({
      x: this.dragStart.positionX + differenceX,
      y: this.dragStart.positionY + differenceY,
    });

    if (this.dragSource === 'header') {
      // El panel se mueve 1:1 con el puntero. Recalcularlo por cuadrante
      // (como la bolita) era lo que provocaba el salto al cruzar el medio.
      this.panelPosition = this.clampPanelPosition({
        x: this.dragStart.panelPositionX + differenceX,
        y: this.dragStart.panelPositionY + differenceY,
      });
    } else {
      this.updatePanelPosition();
    }
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onPointerEnd(event: PointerEvent): void {
    if (
      !this.dragging ||
      this.dragPointerId === null ||
      event.pointerId !== this.dragPointerId
    ) {
      return;
    }

    const draggedFromLauncher = this.dragSource === 'launcher';

    this.dragging = false;
    this.dragPointerId = null;
    this.dragSource = null;

    if (!this.dragMoved) return;

    this.saveFloatingPosition();

    // Evita que al soltar el botón después de arrastrarlo también se abra.
    if (draggedFromLauncher) {
      this.ignoreLauncherClick = true;

      setTimeout(() => {
        this.ignoreLauncherClick = false;
      }, 100);
    }
  }

  moveToNearestCorner(): void {
    const margin = this.getViewportMargin();
    const maxX = Math.max(
      margin,
      window.innerWidth - this.launcherSize - margin,
    );
    const maxY = Math.max(
      margin,
      window.innerHeight - this.launcherSize - margin,
    );

    const launcherCenterX =
      this.floatingPosition.x + this.launcherSize / 2;

    const launcherCenterY =
      this.floatingPosition.y + this.launcherSize / 2;

    this.floatingPosition = {
      x: launcherCenterX <= window.innerWidth / 2 ? margin : maxX,
      y: launcherCenterY <= window.innerHeight / 2 ? margin : maxY,
    };

    this.open = false;
    this.historyOpen = false;

    this.updatePanelPosition();
    this.saveFloatingPosition();
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    this.isCompactViewport = window.innerWidth <= 600;

    this.floatingPosition = this.clampFloatingPosition(
      this.floatingPosition,
    );

    this.updatePanelPosition();
    this.saveFloatingPosition();
  }

  newConversation(confirmReset = true): void {
    if (
      confirmReset &&
      this.messages.some((item) => item.role === 'user') &&
      !window.confirm('¿Iniciar una conversación nueva con Kyara?')
    ) {
      return;
    }
    this.stopGeneration();
    this.conversationId = null;
    this.historyOpen = false;
    this.messages = [
      {
        role: 'assistant',
        content: !this.enabled
          ? 'Kyara está desactivada. Un administrador puede habilitarla desde la configuración.'
          : this.hasAccess
            ? 'Hola. Soy Kyara, la asistente empresarial de ZIVO. Solo consultaré la información autorizada para tu usuario.'
            : this.accessMessage,
        status: this.hasAccess ? undefined : 'access_denied',
        createdAt: new Date(),
      },
    ];
    this.scrollToBottom();
  }

  openAdmin(): void {
    this.open = false;
    this.router.navigate(['/configuraciones/copilot']);
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
    if (!this.historyOpen) return;
    this.copilotService.listConversations().subscribe({
      next: (response) => {
        this.conversations = response.data || [];
      },
      error: () => {
        this.conversations = [];
      },
    });
  }

  openConversation(conversation: CopilotConversationSummary): void {
    this.stopGeneration();
    this.copilotService.getConversation(conversation.id).subscribe({
      next: (response) => {
        this.conversationId = response.data.id;
        this.messages = (response.data.messages || []).map((item) => ({
          role: item.role,
          content: item.content,
          status: this.normalizeStatus(item.status),
          partial: item.safe_metadata?.['partialResult'] === true,
          entities: Array.isArray(item.safe_metadata?.['entitiesUsed'])
            ? (item.safe_metadata?.['entitiesUsed'] as string[])
            : [],
          omittedCount: Number(item.safe_metadata?.['omittedFieldCount'] || 0),
          createdAt: new Date(item.created_at),
        }));
        this.historyOpen = false;
        this.scrollToBottom();
      },
    });
  }

  send(): void {
    const question = this.message.trim();
    if (!question || this.loading || !this.enabled) return;

    this.messages.push({ role: 'user', content: question, createdAt: new Date() });
    this.message = '';
    this.loading = true;
    this.beginLoadingCycle();
    this.scrollToBottom();

    this.activeRequest = this.copilotService
      .chat(question, this.conversationId)
      .subscribe({
        next: (response) => this.handleResponse(response, question),
        error: (error) => {
          const response = error?.error as Partial<CopilotChatResponse> | undefined;
          this.messages.push({
            role: 'assistant',
            content:
              response?.answer ||
              'Kyara no pudo completar la consulta. Puedes reintentarla o reformularla.',
            status: response?.status || 'service_error',
            failedQuestion:
              response?.status === 'access_denied' ? undefined : question,
            createdAt: new Date(),
          });
          this.finishLoading();
        },
      });
  }

  retry(question: string | undefined): void {
    if (!question || this.loading) return;
    this.message = question;
    this.send();
  }

  stopGeneration(): void {
    this.activeRequest?.unsubscribe();
    this.activeRequest = undefined;
    this.finishLoading();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  trackConversation(_index: number, item: CopilotConversationSummary): string {
    return item.id;
  }

  trackMessage(index: number, item: UiMessage): string {
    return `${index}-${item.createdAt.getTime()}`;
  }

  statusLabel(status?: CopilotStatus): string | null {
    const labels: Partial<Record<CopilotStatus, string>> = {
      partial: 'Resultado parcial',
      clarification_required: 'Necesito una aclaración',
      access_denied: 'Sin permisos',
      validation_error: 'Consulta no válida',
      query_limit_exceeded: 'Límite de consulta',
      service_error: 'No se completó',
    };
    return status ? labels[status] || null : null;
  }

  private handleResponse(response: CopilotChatResponse, question: string): void {
    if (response.conversationId) this.conversationId = response.conversationId;
    this.messages.push({
      role: 'assistant',
      content: response.answer,
      status: response.status,
      partial: response.metadata?.partialResult,
      omittedCount: response.metadata?.omittedFields?.length || 0,
      entities: response.metadata?.entitiesUsed || [],
      failedQuestion: response.success ? undefined : question,
      createdAt: new Date(),
    });
    this.finishLoading();
  }

  private beginLoadingCycle(): void {
    const labels = [
      'Kyara analiza tu consulta',
      'Consultando información autorizada',
      'Preparando respuesta',
    ];
    let index = 0;
    this.loadingLabel = labels[index];
    this.statusTimer = setInterval(() => {
      index = Math.min(index + 1, labels.length - 1);
      this.loadingLabel = labels[index];
    }, 1400);
  }

  private finishLoading(): void {
    this.loading = false;
    if (this.statusTimer) clearInterval(this.statusTimer);
    this.statusTimer = undefined;
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const viewport = this.messagesViewport?.nativeElement;
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    });
  }

  private normalizeStatus(status: string): CopilotStatus | undefined {
    const normalized = status.toLowerCase();
    const supported: CopilotStatus[] = [
      'success',
      'partial',
      'clarification_required',
      'access_denied',
      'validation_error',
      'query_limit_exceeded',
      'service_error',
    ];
    return supported.includes(normalized as CopilotStatus)
      ? (normalized as CopilotStatus)
      : undefined;
  }

  private initializeFloatingPosition(): void {
    this.isCompactViewport = window.innerWidth <= 600;

    const savedPosition = this.readFloatingPosition();

    if (savedPosition) {
      this.floatingPosition =
        this.clampFloatingPosition(savedPosition);
    } else {
      const margin = this.getViewportMargin();

      this.floatingPosition = {
        x: Math.max(
          margin,
          window.innerWidth - this.launcherSize - margin,
        ),
        y: Math.max(
          margin,
          window.innerHeight - this.launcherSize - margin,
        ),
      };
    }

    this.updatePanelPosition();
  }

private updatePanelPosition(): void {
  this.isCompactViewport = window.innerWidth <= 600;

  if (this.isCompactViewport) return;

  const { width: panelWidth, height: panelHeight } = this.getPanelDimensions();
  const separation = 14;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const launcherCenterX = this.floatingPosition.x + this.launcherSize / 2;
  const launcherCenterY = this.floatingPosition.y + this.launcherSize / 2;

  let panelX: number;
  let panelY: number;

  if (launcherCenterX > viewportWidth / 2) {
    panelX = this.floatingPosition.x + this.launcherSize - panelWidth;
  } else {
    panelX = this.floatingPosition.x;
  }

  if (launcherCenterY > viewportHeight / 2) {
    panelY = this.floatingPosition.y - separation - panelHeight;
  } else {
    panelY = this.floatingPosition.y + this.launcherSize + separation;
  }

  this.panelPosition = this.clampPanelPosition({ x: panelX, y: panelY });
}

private clampPanelPosition(position: { x: number; y: number }): { x: number; y: number } {
  const { width: panelWidth, height: panelHeight, margin } = this.getPanelDimensions();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  return {
    x: this.clampNumber(position.x, margin, Math.max(margin, viewportWidth - panelWidth - margin)),
    y: this.clampNumber(position.y, margin, Math.max(margin, viewportHeight - panelHeight - margin)),
  };
}

private getPanelDimensions(): { width: number; height: number; margin: number } {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  return {
    width: Math.min(410, viewportWidth - 32),
    height: Math.min(650, viewportHeight - 24),
    margin: 12,
  };
}

  private clampFloatingPosition(position: {
    x: number;
    y: number;
  }): { x: number; y: number } {
    const margin = this.getViewportMargin();

    const maxX = Math.max(
      margin,
      window.innerWidth - this.launcherSize - margin,
    );

    const maxY = Math.max(
      margin,
      window.innerHeight - this.launcherSize - margin,
    );

    return {
      x: this.clampNumber(position.x, margin, maxX),
      y: this.clampNumber(position.y, margin, maxY),
    };
  }

  private saveFloatingPosition(): void {
    try {
      window.localStorage.setItem(
        this.floatingPositionStorageKey,
        JSON.stringify(this.floatingPosition),
      );
    } catch {
      // El widget sigue funcionando aunque localStorage no esté disponible.
    }
  }

  private readFloatingPosition(): {
    x: number;
    y: number;
  } | null {
    try {
      const storedValue = window.localStorage.getItem(
        this.floatingPositionStorageKey,
      );

      if (!storedValue) return null;

      const parsedValue = JSON.parse(storedValue) as {
        x?: unknown;
        y?: unknown;
      };

      const x = Number(parsedValue.x);
      const y = Number(parsedValue.y);

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }

      return { x, y };
    } catch {
      return null;
    }
  }

  private getViewportMargin(): number {
    return this.isCompactViewport ? 12 : 18;
  }

  private clampNumber(
    value: number,
    minimum: number,
    maximum: number,
  ): number {
    return Math.min(Math.max(value, minimum), maximum);
  }
}
